import {
  LockOutlined,
  MailOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Descriptions,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { localizedSystemName, setTenantRuntimeSettings } from '../lib/tenant';
import { usePublicBranding } from '../components/AuthBrand';

type AccountUser = {
  id: string;
  username: string;
  email: string;
  displayName: string;
  locale: 'zh-CN' | 'en-US';
  mustChangePassword: boolean;
  permissions: string[];
  tenant: {
    systemName: string | null;
    systemNameZh?: string | null;
    systemNameEn?: string | null;
    defaultLocale: 'zh-CN' | 'en-US';
    timezone: string;
    contactEmail: string | null;
  };
};

type PasswordForm = {
  username?: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type EmailForm = { email: string; currentPassword: string };

type PreferencesForm = {
  locale: 'zh-CN' | 'en-US';
};

function roleLabel(
  user: AccountUser | undefined,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (!user) return t('account.roleUnknown');
  if (user.permissions.includes('identity:read'))
    return t('account.roleSystemAdmin');
  if (
    user.permissions.includes('inventory:execute') ||
    user.permissions.includes('sample:create')
  )
    return t('account.roleBusinessAdmin');
  return t('account.roleRegularUser');
}

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { passwordMinLength } = usePublicBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [messageApi, holder] = message.useMessage();
  const [user, setUser] = useState<AccountUser>();
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [passwordForm] = Form.useForm<PasswordForm>();
  const [emailForm] = Form.useForm<EmailForm>();
  const activeTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    return tab === 'profile' ? 'profile' : 'settings';
  }, [location.search]);

  const loadUser = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/auth/me', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(t('account.loadFailed'));
      const data = (await response.json()) as AccountUser;
      setUser(data);
      setTenantRuntimeSettings(data.tenant);
    } catch {
      await messageApi.error(t('account.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUser();
  }, []);

  const changePassword = async (values: PasswordForm) => {
    setSavingPassword(true);
    try {
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }),
      });
      if (!response.ok) throw new Error(t('password.saveFailed'));
      passwordForm.resetFields();
      await messageApi.success(t('password.saved'));
      await loadUser();
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('password.saveFailed'),
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const saveEmail = async (values: EmailForm) => {
    setSavingEmail(true);
    try {
      const response = await fetch('/api/v1/auth/me/email', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: values.email.trim(),
          currentPassword: values.currentPassword,
        }),
      });
      if (!response.ok) {
        throw new Error(
          response.status === 409
            ? t('account.emailConflict')
            : response.status === 401
              ? t('account.currentPasswordInvalid')
              : t('account.emailSaveFailed'),
        );
      }
      const data = (await response.json()) as AccountUser;
      setUser(data);
      emailForm.setFieldValue('currentPassword', '');
      await messageApi.success(t('account.emailSaved'));
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('account.emailSaveFailed'),
      );
    } finally {
      setSavingEmail(false);
    }
  };

  const savePreferences = async (values: PreferencesForm) => {
    setSavingPreferences(true);
    try {
      const response = await fetch('/api/v1/auth/me/locale', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ locale: values.locale }),
      });
      if (!response.ok) throw new Error(t('account.preferencesSaveFailed'));
      const data = (await response.json()) as AccountUser;
      setUser(data);
      setTenantRuntimeSettings(data.tenant);
      await i18n.changeLanguage(values.locale);
      await messageApi.success(t('account.preferencesSaved'));
    } catch (error) {
      await messageApi.error(
        error instanceof Error
          ? error.message
          : t('account.preferencesSaveFailed'),
      );
    } finally {
      setSavingPreferences(false);
    }
  };

  return (
    <section className="account-page">
      {holder}
      <div className="account-header">
        <Space size={16} align="center">
          <Avatar size={48} icon={<UserOutlined />} />
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t('account.title')}
            </Typography.Title>
            <Typography.Text type="secondary">
              {user?.displayName ?? user?.username ?? '-'}
            </Typography.Text>
          </div>
        </Space>
        <Tag
          color={user?.permissions.includes('identity:read') ? 'blue' : 'green'}
        >
          {roleLabel(user, t)}
        </Tag>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) =>
          void navigate(`/account?tab=${key}`, { replace: true })
        }
        items={[
          {
            key: 'settings',
            label: t('account.settings'),
            icon: <SettingOutlined />,
            children: (
              <Skeleton active loading={loading} paragraph={{ rows: 6 }}>
                <div className="account-settings-grid">
                  <section className="account-settings-section">
                    <Typography.Title level={5}>
                      {t('password.title')}
                    </Typography.Title>
                    {user?.mustChangePassword ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={t('password.required')}
                        style={{ marginBottom: 20 }}
                      />
                    ) : null}
                    <Form<PasswordForm>
                      form={passwordForm}
                      key={user?.username ?? 'password'}
                      layout="vertical"
                      initialValues={{ username: user?.username }}
                      onFinish={(values) => void changePassword(values)}
                    >
                      <Form.Item name="username" hidden>
                        <Input autoComplete="username" />
                      </Form.Item>
                      <Form.Item
                        name="currentPassword"
                        label={t('password.current')}
                        rules={[{ required: true }]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          autoComplete="current-password"
                        />
                      </Form.Item>
                      <Form.Item
                        name="newPassword"
                        label={t('password.new')}
                        rules={[
                          { required: true },
                          {
                            min: passwordMinLength,
                            message: t('password.rule', {
                              min: passwordMinLength,
                            }),
                          },
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          autoComplete="new-password"
                        />
                      </Form.Item>
                      <Form.Item
                        name="confirmPassword"
                        label={t('password.confirm')}
                        dependencies={['newPassword']}
                        rules={[
                          { required: true },
                          {
                            validator: (_, value) =>
                              !value ||
                              passwordForm.getFieldValue('newPassword') ===
                                value
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(t('password.mismatch')),
                                  ),
                          },
                        ]}
                      >
                        <Input.Password
                          prefix={<LockOutlined />}
                          autoComplete="new-password"
                        />
                      </Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingPassword}
                      >
                        {t('password.submit')}
                      </Button>
                    </Form>
                  </section>
                  <div className="account-settings-stack">
                    <section className="account-settings-section">
                      <Typography.Title level={5}>
                        {t('account.updateEmail')}
                      </Typography.Title>
                      <Form<EmailForm>
                        form={emailForm}
                        key={user?.email ?? 'email'}
                        layout="vertical"
                        initialValues={{ email: user?.email ?? '' }}
                        onFinish={(values) => void saveEmail(values)}
                      >
                        <Form.Item
                          name="email"
                          label={t('account.email')}
                          rules={[{ required: true, type: 'email' }]}
                        >
                          <Input
                            type="email"
                            prefix={<MailOutlined />}
                            autoComplete="email"
                          />
                        </Form.Item>
                        <Form.Item
                          name="currentPassword"
                          label={t('password.current')}
                          rules={[{ required: true }]}
                        >
                          <Input.Password
                            prefix={<LockOutlined />}
                            autoComplete="current-password"
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={savingEmail}
                        >
                          {t('account.saveEmail')}
                        </Button>
                      </Form>
                    </section>
                    <section className="account-settings-section">
                      <Typography.Title level={5}>
                        {t('account.preferences')}
                      </Typography.Title>
                      <Form<PreferencesForm>
                        key={user?.locale ?? 'preferences'}
                        layout="vertical"
                        initialValues={{ locale: user?.locale ?? 'zh-CN' }}
                        onFinish={(values) => void savePreferences(values)}
                      >
                        <Form.Item name="locale" label={t('account.locale')}>
                          <Select
                            options={[
                              { value: 'zh-CN', label: '中文' },
                              { value: 'en-US', label: 'English' },
                            ]}
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={savingPreferences}
                        >
                          {t('common.save')}
                        </Button>
                      </Form>
                    </section>
                  </div>
                </div>
              </Skeleton>
            ),
          },
          {
            key: 'profile',
            label: t('account.profile'),
            icon: <UserOutlined />,
            children: (
              <Skeleton active loading={loading} paragraph={{ rows: 4 }}>
                <Descriptions bordered column={{ xs: 1, md: 2 }} size="medium">
                  <Descriptions.Item label={t('account.username')}>
                    {user?.username ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.email')}>
                    {user?.email ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.role')}>
                    {roleLabel(user, t)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.locale')}>
                    {user?.locale === 'en-US' ? 'English' : '中文'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.timezone')}>
                    {user?.tenant.timezone ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.systemName')}>
                    {user
                      ? localizedSystemName(
                          user.tenant,
                          i18n.resolvedLanguage,
                          t('app.name'),
                        )
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('account.contactEmail')}>
                    {user?.tenant.contactEmail ?? '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Skeleton>
            ),
          },
        ]}
      />
    </section>
  );
}
